import Button from "@ui-kit/form/Button/Button.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Input from "@ui-kit/form/Input/Input.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import {
  NEWSLETTER_ACTION_URL,
  NEWSLETTER_LID,
  NEWSLETTER_MID,
  NEWSLETTER_THX_URL,
  NEWSLETTER_ERR_URL,
} from "@shared/env";
import styled from "@emotion/styled";
import { Formik } from "formik";
import breakpoints from "@shared/theme/props/breakpoints";
import { useTranslation } from "@shared/utils/internationalization";
import { useRef } from "react";
import * as yup from "yup";
import { NewsletterSignupProps } from "./NewsletterSignup.props";

const { sm } = breakpoints;

const StyledBox = styled(Box)`
  color: var(--clr-font-light);

  @media only screen and (min-width: ${sm}px) {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    justify-content: flex-end;
  }
`;

const InputContainer = styled.div`
color: var(--clr-font-light);

  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: end;
  width: 100%;
  margin: 0 auto;
  min-width: 202px;

  form {
    display: flex;
  }

  @media only screen and (max-width: ${sm}px) {
    form{
      justify-content: center;
      width:100%;
    }
  }

  @media only screen and (min-width: ${sm}px) {
    margin: unset;
    max-width: 315px;
    justify-content: center;
  }

 .field-shift {
    left: -9999px; position: absolute;
  }
}
`;

const CTAText = styled.p`
  text-align: center;
  font-size: 14px;
  line-height: 14px;
  margin-bottom: 22px;
  color: var(--clr-font-light);

  @media only screen and (min-width: ${sm}px) {
    text-align: right;
    font-size: 14px;
    display: inline-block;
    max-width: 205px;
    margin-bottom: 0;
    margin-right: 16px;
    padding-top: 5px;
  }
`;

const StyledInput = styled(Input)`
  border: none;
  color: var(--clr-font-light);
  background-color: var(--clr-paint-primary-600);
  display: inline-block;
  height: 40px;

  ::placeholder {
    color: var(--clr-font-light);
  }

  @media only screen and (min-width: ${sm}px) {
    max-width: 202px;
  }
`;

const StyledButton = styled(Button)`
  flex-grow: 1;
  line-height: 30px;
  min-width: 125px;

  @media only screen and (min-width: ${sm}px) {
    width: 50%;
    max-width: 147px;
  }
`;

const NewsletterSignup = ({
  title,
  buttonText,
  placeholder,
}: NewsletterSignupProps) => {
  const { t } = useTranslation("common");

  const formEl = useRef(null);

  const doHtmlFormPost = () => {
    formEl.current.submit();
  };

  const newsletterSignUpValidationSchema = yup.object().shape({
    "Email Address": yup
      .string()
      .required(t("Email is required"))
      .email(t("Invalid email address")),
  });
  return (
    <StyledBox>
      <CTAText>{title}</CTAText>
      <InputContainer>
        <Formik
          initialValues={{ "Email Address": "" }}
          onSubmit={() => {
            doHtmlFormPost();
          }}
          validationSchema={newsletterSignUpValidationSchema}
        >
          {(props) => (
            <Flex flexDirection="column" width="100%">
              <form
                name="subscribeForm"
                action={NEWSLETTER_ACTION_URL}
                ref={formEl}
                onSubmit={props.handleSubmit}
                method="post"
                noValidate
              >
                <input type="hidden" name="thx" value={NEWSLETTER_THX_URL} />
                <input type="hidden" name="err" value={NEWSLETTER_ERR_URL} />
                <input type="hidden" name="SubAction" value="sub" />
                <input type="hidden" name="MID" value={NEWSLETTER_MID} />
                <input type="hidden" name="lid" value={NEWSLETTER_LID} />
                <input type="hidden" name="Email Type" value="HTML" />

                <StyledInput
                  type="text"
                  name="Email Address"
                  value={props.values["Email Address"]}
                  aria-label={placeholder}
                  onChange={props.handleChange}
                  placeholder={placeholder}
                  size="sm"
                />

                <Box minW="element.md" />

                <StyledButton
                  data-testid="newsletter-signup-button"
                  type="submit"
                  value={buttonText}
                  className="button"
                  colorPalette="light"
                  color="font.dark"
                  variant="solid"
                >
                  {buttonText}
                </StyledButton>
              </form>
              <Text>{props.errors["Email Address"]}</Text>
            </Flex>
          )}
        </Formik>
      </InputContainer>
    </StyledBox>
  );
};

export default NewsletterSignup;
